import { Link, Text } from '@fluentui/react-components';
import { ChevronDown12Regular, ChevronUp12Regular } from '@fluentui/react-icons';
import { truncate } from 'es-toolkit/compat';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { CommonLabelAndValueList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';

const MAX_DESCRIPTION_LENGTH = 50;

const CustomerReferencesSectionImpl: React.FC = () => {
  const {
    reviewAndSubmitNominationPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const handleToggleCustomerReferenceDescription = (isExpanded: boolean, customerReferenceId: number): void => {
    isExpanded
      ? reviewAndSubmitNominationPageUiStore.collapseCustomerReferenceDescription(customerReferenceId)
      : reviewAndSubmitNominationPageUiStore.expandCustomerReferenceDescription(customerReferenceId);
  };

  return (
    <FlexBox
      wrap
      isFullWidth
      as='section'
      className='nomination-section'
    >
      <Text
        as='h3'
        block
        weight='semibold'
        size={400}
      >
        Customer reference
      </Text>
      {
        reviewAndSubmitNominationPageUiStore.currentNomination?.customerReferences.map((customerReference, index) => {
          const isExpanded = reviewAndSubmitNominationPageUiStore.isCustomerReferenceDescriptionExpanded(
            customerReference.customerReferenceId,
          );
          const isTruncatable = customerReference.description.length > MAX_DESCRIPTION_LENGTH;
          const displayDescription = isExpanded
            ? customerReference.description
            : truncate(customerReference.description, {
              length: MAX_DESCRIPTION_LENGTH,
              omission: ' ...',
            });

          return (
            <FlexBox
              wrap
              key={index}
              isFullWidth
              direction='column'
              className='nomination-section__list'
            >
              <FlexBox
                isFullWidth
                aligns='start'
              >
                <Text
                  weight='semibold'
                >
                  Customer reference #{index + 1}
                </Text>
              </FlexBox>
              <FlexBox
                isFullWidth
                aligns='start'
              >
                <CommonLabelAndValueList
                  label='Prefix:'
                  childrenFallback={customerReference.prefix}
                />
                <CommonLabelAndValueList
                  label='First Name:'
                  childrenFallback={customerReference.firstName}
                />
              </FlexBox>
              <FlexBox
                isFullWidth
                aligns='start'
              >
                <CommonLabelAndValueList
                  label='Last name:'
                  childrenFallback={customerReference.lastName}
                />
                <CommonLabelAndValueList
                  label='Title / Role:'
                  childrenFallback={customerReference.title}
                />
              </FlexBox>
              <FlexBox
                isFullWidth
                aligns='start'
              >
                <CommonLabelAndValueList
                  label='Email:'
                  childrenFallback={customerReference.emailAddress}
                />
                <CommonLabelAndValueList
                  label='Phone number:'
                  childrenFallback={customerReference.phoneNumber}
                />
              </FlexBox>
              <FlexBox
                isFullWidth
                aligns='start'
              >
                <CommonLabelAndValueList
                  label='Company:'
                  childrenFallback={customerReference.companyName}
                />
              </FlexBox>
              <FlexBox
                isFullWidth
                aligns='start'
              >
                <CommonLabelAndValueList
                  label='Brief description:'
                >
                  <FlexBox
                    direction='column'
                    aligns='start'
                  >
                    <Text
                      block
                      weight='semibold'
                      align='start'
                      as='p'
                    >
                      {displayDescription}
                    </Text>
                    {
                      isTruncatable &&
                      <Link
                        as='button'
                        aria-expanded={isExpanded}
                        className={commonStyles.buttonAsLink}
                        onClick={() => handleToggleCustomerReferenceDescription(isExpanded, customerReference.customerReferenceId)}
                      >
                        {isExpanded ? <>
                          Show less
                          <ChevronUp12Regular />
                        </> : <>
                          Show more
                          <ChevronDown12Regular />
                        </>
                        }
                      </Link>
                    }
                  </FlexBox>
                </CommonLabelAndValueList>
              </FlexBox>
            </FlexBox>
          );
        })
      }
    </FlexBox>
  );
}

export const CustomerReferencesSection = observer(CustomerReferencesSectionImpl);
